/**
 * 迷你按钮 / 框体「拖动落点」+ 提示的浏览器缓存。
 *
 * - 位置(BubblePlacement)按 (clientId, slot) 隔离落 localStorage,统一带写入时间戳信封
 *   ({ p, at }),read 时按 24h 有效期(POSITION_TTL_MS)判过期 —— 含迷你气泡、网页面板
 *   普通态/最大化态、数字人桌面弹窗四个槽位,各槽独立记忆、独立过期。
 * - 「拖动提示是否已展示」时间戳单独存(HINT_PREFIX),1h 冷却(DRAG_HINT_COOLDOWN_MS),
 *   与位置有效期相互独立。
 * - 所有访问 try-catch 兜底隐私模式 / 第三方存储拦截(与 main.tsx / i18n 既有约定一致)。
 * - 位置覆盖只作用于对应框体(组件层用 inline style),不改服务端配置;无 / 过期缓存时
 *   回落到服务端配置驱动的 CSS 变量 / CSS 默认布局 —— 满足「优先缓存,否则默认」。
 */

/** 拖动落点:就近角锚定 + 距锚定两边的 px 偏移(缩放后框体仍贴近原角且可见) */
export interface BubblePlacement {
  /** 水平锚:框体中心落在左半屏=left,否则 right */
  ax: 'left' | 'right';
  /** 垂直锚:框体中心落在上半屏=top,否则 bottom */
  ay: 'top' | 'bottom';
  /** 距 ax 边的 px(>=0) */
  x: number;
  /** 距 ay 边的 px(>=0) */
  y: number;
}

/**
 * 位置记录槽位:各槽独立存储、独立 24h 过期。
 *  - bubble:        迷你气泡(web/digital 两线共用,沿用旧 key)
 *  - web-panel:     网页面板「普通停靠态」
 *  - web-panel-max: 网页面板「最大化态(居中大窗)」
 *  - digital-modal: 数字人桌面居中弹窗
 */
export type PositionSlot = 'bubble' | 'web-panel' | 'web-panel-max' | 'digital-modal';

const POS_PREFIX = 'dcs.widget.pos.';
const HINT_PREFIX = 'dcs.widget.drag_hint_at.';

/** 提示重复展示的冷却窗口:1 小时(与位置有效期 POSITION_TTL_MS 相互独立) */
export const DRAG_HINT_COOLDOWN_MS = 60 * 60 * 1000;

/** 位置记录统一有效期:24 小时。所有槽位(bubble/web-panel/web-panel-max/digital-modal)共用。 */
export const POSITION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * bubble 槽沿用旧 key `dcs.widget.pos.${clientId}`(兼容旧裸数据所在位置);
 * 其余槽用 `dcs.widget.pos.${slot}.${clientId}` 段隔离,绝不与旧数据碰撞。
 */
function slotKey(clientId: string, slot: PositionSlot): string {
  return slot === 'bubble'
    ? `${POS_PREFIX}${clientId}`
    : `${POS_PREFIX}${slot}.${clientId}`;
}

function hintKey(clientId: string): string {
  return `${HINT_PREFIX}${clientId}`;
}

/** 形状校验:容忍任意 JSON,只接受完整合法的 BubblePlacement */
function isPlacement(v: unknown): v is BubblePlacement {
  if (!v || typeof v !== 'object') return false;
  const p = v as Record<string, unknown>;
  return (
    (p.ax === 'left' || p.ax === 'right') &&
    (p.ay === 'top' || p.ay === 'bottom') &&
    typeof p.x === 'number' &&
    Number.isFinite(p.x) &&
    typeof p.y === 'number' &&
    Number.isFinite(p.y)
  );
}

/** 带写入时间戳的位置信封(localStorage 持久层格式) */
interface PlacementEnvelope {
  /** 位置 */
  p: BubblePlacement;
  /** 写入时刻(ms);read 时按 now - at >= POSITION_TTL_MS 判过期 */
  at: number;
}

/** 信封形状校验:{ p: 合法 BubblePlacement; at: 有限数 }。旧裸数据 / 缺 at 一律不通过。 */
function isEnvelope(v: unknown): v is PlacementEnvelope {
  if (!v || typeof v !== 'object') return false;
  const e = v as Record<string, unknown>;
  return typeof e.at === 'number' && Number.isFinite(e.at) && isPlacement(e.p);
}

/**
 * 读某槽缓存位置;无 / 非法 / 隐私模式 / 旧裸数据(缺时间戳) / 已过期(now-at>=TTL) → null
 * (调用方回落服务端配置 / CSS 默认布局)。now 由调用方注入,便于单测注入固定时刻。
 *
 * 向后兼容:旧版 `dcs.widget.pos.${clientId}` 存的是裸 BubblePlacement(无 at),
 * 命中「非信封」分支 → 当过期丢弃返回 null;下次拖动以信封写回,平滑升级。
 */
export function readPlacement(
  clientId: string | null,
  slot: PositionSlot,
  now: number,
): BubblePlacement | null {
  if (!clientId) return null;
  try {
    const raw = localStorage.getItem(slotKey(clientId, slot));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isEnvelope(parsed)) return null; // 旧裸数据 / 脏数据 → 当过期丢弃
    if (now - parsed.at >= POSITION_TTL_MS) return null; // 24h 过期
    return parsed.p;
  } catch {
    return null;
  }
}

/** 写某槽缓存位置(带 now 时间戳信封);隐私模式静默失败(本次会话内仍由信号驱动生效)。 */
export function writePlacement(
  clientId: string | null,
  slot: PositionSlot,
  p: BubblePlacement,
  now: number,
): void {
  if (!clientId) return;
  try {
    const env: PlacementEnvelope = { p, at: now };
    localStorage.setItem(slotKey(clientId, slot), JSON.stringify(env));
  } catch {
    /* ignore */
  }
}

/**
 * 读 bubble 槽缓存位置(薄封装,内部走 readPlacement('bubble'));
 * 无 / 非法 / 隐私模式 / 已过期 → null(调用方回落服务端配置)。
 */
export function readStoredPlacement(clientId: string | null): BubblePlacement | null {
  return readPlacement(clientId, 'bubble', Date.now());
}

/** 落手时写 bubble 槽缓存位置(薄封装,内部走 writePlacement('bubble'))。 */
export function writeStoredPlacement(clientId: string | null, p: BubblePlacement): void {
  writePlacement(clientId, 'bubble', p, Date.now());
}

/**
 * 是否展示「长按可拖动」提示:
 *  - 已有(未过期的)缓存位置(用户拖过) → 永不再提示;
 *  - 否则 1 小时内已提示过 → 不提示;
 *  - 其余(首次出现 / 距上次提示超 1h / 位置已过期) → 提示。
 * now 由调用方注入(便于单测)。
 * 注:bubble 位置 24h 过期后此处转为可提示,与「位置回默认」体验自洽。
 */
export function shouldShowDragHint(clientId: string | null, now: number): boolean {
  if (readPlacement(clientId, 'bubble', now) !== null) return false;
  if (!clientId) return true; // 拿不到 clientId(隐私模式等):至少首次提示,不做持久抑制
  try {
    const raw = localStorage.getItem(hintKey(clientId));
    if (!raw) return true;
    const at = Number(raw);
    if (!Number.isFinite(at)) return true;
    return now - at >= DRAG_HINT_COOLDOWN_MS;
  } catch {
    return true;
  }
}

/** 记录提示已在 now 展示(进入冷却窗口) */
export function markDragHintShown(clientId: string | null, now: number): void {
  if (!clientId) return;
  try {
    localStorage.setItem(hintKey(clientId), String(now));
  } catch {
    /* ignore */
  }
}
